<template>
  <div class="notification-manager">
    <h2>通知管理</h2>
    
    <!-- 操作栏 -->
    <div class="action-bar">
      <button class="btn-primary" @click="showAddNotification">
        <span>+</span> 发布通知
      </button>
      <button class="btn-danger" @click="batchDelete" :disabled="selectedIds.length === 0">
        批量删除 ({{ selectedIds.length }})
      </button>
      <div class="filter-group">
        <select v-model="filterType" @change="fetchNotifications">
          <option value="all">全部通知</option>
          <option value="global">全局通知</option>
          <option value="personal">个人通知</option>
        </select>
        <input 
          v-model="searchKeyword" 
          placeholder="搜索标题或内容"
          @keyup.enter="fetchNotifications"
        />
        <button @click="fetchNotifications">搜索</button>
      </div>
    </div>

    <!-- 通知列表表格 -->
    <div class="table-wrapper">
      <table class="data-table" v-if="notifications.length">
        <thead>
          <tr>
            <th width="40">
              <input type="checkbox" @change="toggleAll" v-model="selectAll" />
            </th>
            <th width="20">ID</th>
            <th>标题</th>
            <th>内容</th>
            <th width="80">通知类型</th>
            <th >接收用户</th>
            <th >发送时间</th>
            <th width="100">阅读状态</th>
            <th width="140">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in notifications" :key="item.id">
            <td style="text-align: center">
              <input type="checkbox" v-model="selectedIds" :value="item.id" />
            </td>
            <td>{{ item.id }}</td>
            <td class="title-cell">{{ item.title }}</td>
            <td class="content-cell">{{ truncateContent(item.content) }}</td>
            <td>
              <span :class="['badge', item.is_global == 1 ? 'badge-global' : 'badge-personal']">
                {{ item.is_global == 1 ? '全局通知' : '个人通知' }}
              </span>
            </td>
            <td>
              <span v-if="item.is_global != 1 && item.openid" class="openid-cell">
                {{ item.openid.substring(0, 15) }}...
              </span>
              <span v-else class="all-users">所有用户</span>
            </td>
            <td>{{ formatTime(item.time) }}</td>
            <td>
              <div class="read-stats">
                <span class="read-count">已读: {{ getReadCount(item) }}</span>
                <span class="unread-count">未读: {{ getUnreadCount(item) }}</span>
              </div>
            </td>
            <td>
              <button class="btn-view" @click="viewDetail(item)" title="查看详情">👁️</button>
              <button class="btn-stats" @click="viewStats(item)" title="阅读统计">📊</button>
              <button class="btn-delete" @click="deleteNotification(item.id)" title="删除">🗑️</button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div v-else class="empty-state">
        <p>暂无通知数据</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination" v-if="total > 0">
      <button @click="prevPage" :disabled="page <= 1">上一页</button>
      <span>第 {{ page }} / {{ totalPages }} 页</span>
      <span>共 {{ total }} 条</span>
      <button @click="nextPage" :disabled="page >= totalPages">下一页</button>
    </div>

    <!-- 发布/编辑通知弹窗 -->
    <div v-if="showModal" @click.self="closeModal" class="modal-overlay">
      <div class="modal">
        <h3>{{ isEdit ? '编辑通知' : '发布通知' }}</h3>

        
        <div class="form-group">
          <label>通知标题 <span class="required">*</span></label>
          <input v-model="formData.title" type="text" placeholder="请输入通知标题" maxlength="255" />
        </div>
        
        <div class="form-group">
          <label>通知类型</label>
          <div class="radio-group">
            <label>
              <input type="radio" v-model="formData.is_global" :value="1" />
              全局通知（发送给所有用户）
            </label>
            <label>
              <input type="radio" v-model="formData.is_global" :value="0" />
              个人通知（指定用户）
            </label>
          </div>
        </div>
        
        <div class="form-group" v-if="formData.is_global === 0">
          <label>接收用户 <span class="required">*</span></label>
          <textarea 
            v-model="openidList" 
            placeholder="请输入用户的openid，多个用逗号、空格或换行分隔"
            rows="3"
          ></textarea>
          <div class="form-hint">
            支持批量发送，多个openid请用逗号、空格或换行分隔
          </div>
        </div>
        
        <div class="form-group">
          <label>通知内容 <span class="required">*</span></label>
          <textarea 
            v-model="formData.content" 
            placeholder="请输入通知内容"
            rows="6"
          ></textarea>
        </div>
        
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">取消</button>
          <button class="btn-submit" @click="submitNotification">发布</button>
        </div>
      </div>
    </div>

   <!-- 阅读统计详情弹窗 -->
<div v-if="showStatsModal" @click.self="closeStatsModal" class="modal-overlay">
  <div class="modal stats-modal">
    <div class="modal-header">
      <h3>阅读统计详情</h3>
      <button class="modal-close" @click="closeStatsModal">×</button>
    </div>
    
    <div class="modal-body">
      <!-- 加载中状态 -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>加载中...</p>
      </div>
      
      <!-- 统计信息 -->
      <div v-else>
        <div class="stats-info">
          <div class="stats-item">
            <span class="stats-label">通知标题：</span>
            <span class="stats-value">{{ currentNotification.title }}</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">发送时间：</span>
            <span class="stats-value">{{ formatTime(currentNotification.time) }}</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">已读用户：</span>
            <span class="stats-value">{{ readCount }} 人</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">未读用户：</span>
            <span class="stats-value">{{ unreadCount }} 人</span>
          </div>
          <div class="stats-item" v-if="currentNotification.is_global == 1">
            <span class="stats-label">总用户数：</span>
            <span class="stats-value">{{ totalUsers }} 人</span>
          </div>
        </div>
        
        <!-- 已读用户列表 - 只在有数据时显示 -->
        <div class="stats-list" v-if="readUsers.length > 0">
          <h4>已读用户列表 <span class="user-count">({{ readUsers.length }})</span></h4>
          <div class="user-list-scroll">
            <div v-for="(openid, index) in readUsers" :key="index" class="user-item">
              <span class="user-index">{{ index + 1 }}</span>
              <span class="user-openid">{{ openid }}</span>
            </div>
          </div>
        </div>
        
        <!-- 暂无已读用户 -->
        <div v-else class="empty-readers">
          <div class="empty-icon">👀</div>
          <p>暂无用户已读此通知</p>
        </div>
      </div>
    </div>
    </div>
    
   
  </div>
</div>
</template>
<script>
import {
  getNotificationList,
  publishNotification,
  deleteNotification,
  batchDeleteNotifications,
  getNotificationStats
} from '@/api/houtai/notifications.js'

export default {
  name: "NotificationManager",
  data() {
  return {
    notifications: [],
    page: 1,
    pageSize: 20,
    total: 0,
    selectedIds: [],
    selectAll: false,
    filterType: "all",
    searchKeyword: "",
    showModal: false,
    showStatsModal: false,
    isEdit: false,
    currentNotification: null,
    readUsers: [],
    readCount: 0,
    unreadCount: 0,
    totalUsers: 0,
    loading: false,
    formData: {
      title: "",
      content: "",
      is_global: 1,
      openid: ""
    },
    openidList: ""
  };
},
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    }
  },
  methods: {
    // 获取通知列表
    async fetchNotifications() {
      try {
        const res = await getNotificationList(
          this.page, 
          this.pageSize, 
          this.filterType, 
          this.searchKeyword
        );

        console.log("通知列表:", res);

        // 兼容多种返回格式
        if (res.status === 200) {
          this.notifications = res.data || [];
          this.total = res.total || 0;
        } else if (res.code === 0) {
          this.notifications = res.data || [];
          this.total = res.total || 0;
        } else if (Array.isArray(res)) {
          this.notifications = res;
          this.total = res.length;
        } else {
          this.notifications = [];
          this.total = 0;
        }
      } catch (error) {
        console.error("获取通知列表失败:", error);
        alert("获取通知列表失败");
      }
    },
    
    // 发布通知
    async submitNotification() {
      if (!this.formData.title.trim()) {
        alert("请输入通知标题");
        return;
      }
      if (!this.formData.content.trim()) {
        alert("请输入通知内容");
        return;
      }
      
      // 处理个人通知的openid列表
      let openids = [];
      if (this.formData.is_global === 0) {
        if (!this.openidList.trim()) {
          alert("请输入接收用户的openid");
          return;
        }
        openids = this.openidList.split(/[,\s\n]+/).filter(openid => openid.trim());
        if (openids.length === 0) {
          alert("请输入有效的openid");
          return;
        }
      }
      
      try {
        const data = {
          title: this.formData.title,
          content: this.formData.content,
          is_global: this.formData.is_global,
          openids: openids
        };
        
        const res = await publishNotification(data);
        
        if (res.code === 0) {
          alert(res.msg || "发布成功");
          this.closeModal();
          this.fetchNotifications();
        } else {
          alert(res.msg || "发布失败");
        }
      } catch (error) {
        console.error("发布通知失败:", error);
        alert("发布通知失败");
      }
    },
    
    // 删除通知
    async deleteNotification(id) {
      if (!confirm("确定要删除这条通知吗？")) return;
      
      try {
        const res = await deleteNotification(id);
        if (res.code === 0 || res.code === 200) {
          alert("删除成功");
          if (this.notifications.length === 1 && this.page > 1) {
            this.page--;
          }
          this.fetchNotifications();
        } else {
          alert(res.msg || "删除失败");
        }
      } catch (error) {
        console.error("删除通知失败:", error);
        alert("删除通知失败");
      }
    },
    
    // 批量删除
    async batchDelete() {
      if (this.selectedIds.length === 0) return;
      if (!confirm(`确定要删除选中的 ${this.selectedIds.length} 条通知吗？`)) return;
      
      try {
        const res = await batchDeleteNotifications(this.selectedIds);
        if (res.code === 0 || res.code === 200) {
          alert("删除成功");
          this.selectedIds = [];
          this.selectAll = false;
          this.fetchNotifications();
        } else {
          alert(res.msg || "删除失败");
        }
      } catch (error) {
        console.error("批量删除失败:", error);
        alert("批量删除失败");
      }
    },
    
    // 查看详情
    viewDetail(item) {
      this.isEdit = true;
      this.formData = {
        title: item.title,
        content: item.content,
        is_global: item.is_global,
        openid: item.openid || ""
      };
      this.openidList = item.openid || "";
      this.showModal = true;
    },
    
    // 阅读统计
   // 阅读统计 - 点击时才加载数据
async viewStats(item) {
  this.currentNotification = item;
  this.showStatsModal = true;
  this.loading = true;
  
  try {
    const res = await getNotificationStats(item.id);
    if (res.status === 200) {
      this.readUsers = res.readUsers || [];
      this.readCount = res.readCount || 0;
      this.unreadCount = res.unreadCount || 0;
      this.totalUsers = res.totalUsers || 0;
    } else {
      alert("获取统计失败");
    }
  } catch (error) {
    console.error("获取阅读统计失败:", error);
    alert("获取阅读统计失败");
  } finally {
    this.loading = false;
  }
},

// 关闭统计弹窗
closeStatsModal() {
  this.showStatsModal = false;
  this.currentNotification = null;
  this.readUsers = [];
  this.readCount = 0;
  this.unreadCount = 0;
  this.totalUsers = 0;
},
    
   // 获取已读数量
getReadCount(item) {
  return item.readCount || 0;
},
    
    // 获取未读数量
getUnreadCount(item) {
  if (item.is_global == 1) {
    return item.unreadCount || '?';
  } else {
    return item.unreadCount || 0;
  }
},
    
    // 全选/取消全选
    toggleAll() {
      if (this.selectAll) {
        this.selectedIds = this.notifications.map(item => item.id);
      } else {
        this.selectedIds = [];
      }
    },
    
    // 显示发布弹窗
    showAddNotification() {
      this.isEdit = false;
      this.formData = {
        title: "",
        content: "",
        is_global: 1,
        openid: ""
      };
      this.openidList = "";
      this.showModal = true;
    },
    
    // 关闭弹窗
    closeModal() {
      this.showModal = false;
      this.formData = {
        title: "",
        content: "",
        is_global: 1,
        openid: ""
      };
      this.openidList = "";
    },
 
    
    // 格式化时间
    formatTime(time) {
      if (!time) return "";
      const date = new Date(time);
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    },
    
    // 截断内容
    truncateContent(content) {
      if (!content) return "";
      return content.length > 50 ? content.substring(0, 50) + "..." : content;
    },
    
    // 分页
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchNotifications();
      }
    },
    
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.fetchNotifications();
      }
    }
  },
  
  watch: {
    selectAll(val) {
      if (val) {
        this.selectedIds = this.notifications.map(item => item.id);
      } else {
        this.selectedIds = [];
      }
    }
  },
  
  mounted() {
    this.fetchNotifications();
  }
};
</script>

<style scoped>
.stats-modall{
  position: relative;
}
.modal-close{
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  background-color: red;
}

.notification-manager {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

h2 {
  color: #333;
  margin-bottom: 20px;
}

.action-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
}

.btn-danger:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.filter-group {
  display: flex;
  gap: 10px;
  flex: 1;
  max-width: 500px;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.filter-group input {
  flex: 1;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 1px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}

.data-table th {
  background: #a1aef5;
  color: white;
  padding: 12px 0;
}

.data-table tbody tr:hover {
  background-color: #f8f9ff;
}

.title-cell {
  font-weight: 500;
  color: #333;
  min-width: 48px;
}

.content-cell {
  color: #666;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
}

.badge-global {
  background-color: #d4edda;
  color: #155724;
}

.badge-personal {
  background-color: #d1ecf1;
  color: #0c5460;
}

.openid-cell {
  font-family: monospace;
  font-size: 11px;
  color: #666;
}

.all-users {
  color: #28a745;
  font-weight: 500;
}

.read-stats {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.read-count {
  color: #28a745;
}

.unread-count {
  color: #dc3545;
}

.btn-view,
.btn-stats,
.btn-delete {
  padding: 5px 10px;
  margin: 0 3px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-view {
  background-color: #17a2b8;
  color: white;
}

.btn-view:hover {
  background-color: #138496;
}

.btn-stats {
  background-color: #ffc107;
  color: #333;
}

.btn-stats:hover {
  background-color: #e0a800;
}

.btn-delete {
  background-color: #dc3545;
  color: white;
}

.btn-delete:hover {
  background-color: #c82333;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}

.pagination button {
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination button:hover:not(:disabled) {
  background-color: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}






.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 主弹窗样式 */
.modal {
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: 20px;
  padding: 0;
  width: 750px;
  max-width: 90%;
  max-height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(102, 126, 234, 0.1);
  animation: slideUp 0.3s ease;
  display: flex;
  flex-direction: column;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal h3 {
  color: #667eea;
  text-align: center;
  font-size: 18px;
  letter-spacing: 0.5px;
  padding-bottom: 20px;
}

/* 统计弹窗样式 */
.stats-modal {
  width: 650px;
}

/* 弹窗内容区域 */
.modal-content {
  padding: 24px;
}

/* 表单组样式优化 */
.form-group {
  margin-bottom: 22px;
  padding: 0 24px;
  display: flex;
  width: 100%;
  padding: 0 30px;
  box-sizing: border-box;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  min-width: 80px;
}

.required {
  color: #ff6b6b;
  margin-left: 4px;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e8ecf1;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: white;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

/* 单选框组样式 */
.radio-group {
  display: flex;
  gap: 24px;
  padding: 8px 0;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  transition: color 0.3s ease;
}

.radio-group label:hover {
  color: #667eea;
}

.radio-group input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #667eea;
}

/* 表单提示文字 */
.form-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-hint::before {
  content: "💡";
  font-size: 12px;
}

/* 弹窗底部按钮 */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  background: #f8f9fc;
  border-top: 1px solid #e8ecf1;
}

.btn-cancel,
.btn-submit {
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-cancel {
  background-color: #f0f2f5;
  color: #666;
}

.btn-cancel:hover {
  background-color: #e4e7eb;
  transform: translateY(-1px);
}

.btn-submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-submit:active {
  transform: translateY(0);
}

/* 统计弹窗样式优化 */
.stats-info {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  padding: 20px;
  border-radius: 16px;
  margin: 24px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.stats-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed rgba(102, 126, 234, 0.2);
}

.stats-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.stats-label {
  font-weight: 600;
  color: #555;
  font-size: 14px;
}

.stats-value {
  color: #667eea;
  font-weight: 600;
  font-size: 16px;
}

.stats-list {
  margin: 24px;
}

.stats-list h4 {
  margin-bottom: 16px;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stats-list h4::before {
  content: "👥";
  font-size: 18px;
}

.user-list-scroll {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  background: white;
}

.user-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
  color: #555;
  transition: all 0.2s ease;
}

.user-item:hover {
  background: linear-gradient(90deg, #f8f9ff 0%, #fff 100%);
  padding-left: 20px;
}

.user-item:last-child {
  border-bottom: none;
}

/* 关闭按钮 */
.btn-close {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: 0 24px 24px 24px;
  width: calc(100% - 48px);
  padding: 12px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-close:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 自定义滚动条 */
.user-list-scroll::-webkit-scrollbar {
  width: 6px;
}

.user-list-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.user-list-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

.user-list-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .modal {
    width: 95%;
    max-height: 90vh;
  }
  
  .stats-modal {
    width: 95%;
  }
  
  .form-group {
    padding: 0 20px;
  }
  
  .stats-info {
    margin: 20px;
    padding: 16px;
  }
  
  .stats-list {
    margin: 20px;
  }
  
  .btn-close {
    margin: 0 20px 20px 20px;
    width: calc(100% - 40px);
  }
  
  .modal-footer {
    padding: 16px 20px;
  }
  
  .radio-group {
    flex-direction: column;
    gap: 12px;
  }
}











.btn-cancel,
.btn-submit,
.btn-close {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-cancel {
  background-color: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background-color: #5a6268;
}

.btn-submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-submit:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.btn-close {
  background-color: #007bff;
  color: white;
  margin-top: 20px;
  width: 100%;
}

.btn-close:hover {
  background-color: #0056b3;
}

.stats-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.stats-item {
  margin-bottom: 10px;
}

.stats-label {
  font-weight: 600;
  color: #333;
  margin-right: 10px;
}

.stats-value {
  color: #666;
}

.stats-list h4 {
  margin-bottom: 10px;
  color: #333;
}

.user-list-scroll {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}

.user-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}

.user-item:hover {
  background-color: #f5f5f5;
}

.empty-state {
  text-align: center;
  padding: 60px;
  color: #999;
}

/* 响应式 */
@media (max-width: 768px) {
  .notification-manager {
    padding: 12px;
  }
  
  .action-bar {
    flex-direction: column;
  }
  
  .filter-group {
    max-width: 100%;
  }
  
  .modal {
    width: 95%;
    padding: 15px;
  }
  
  .stats-modal {
    width: 95%;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px;
    font-size: 12px;
  }
  
  .btn-view,
  .btn-stats,
  .btn-delete {
    padding: 4px 8px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .radio-group {
    flex-direction: column;
    gap: 10px;
  }
}
/* 移动端隐藏接收用户列 */
@media (max-width: 768px) {
  /* 隐藏表头的第6列（接收用户） */
  .data-table th:nth-child(6),
  /* 隐藏每行的第6个单元格 */
  .data-table td:nth-child(6) {
    display: none;
  }
   .data-table th:nth-child(4),
  /* 隐藏每行的第6个单元格 */
  .data-table td:nth-child(4) {
    display: none;
  }
  .modal {
    width: 75%;
  }
}
</style>